import React from 'react';
import { Activity, Layers, Video, FileText, Shield, ArrowRight, Brain, Zap, MonitorPlay } from 'lucide-react';

interface LandingPageProps {
  onLaunch: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onLaunch }) => {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-cyan-500/30">
      
      {/* Navigation */}
      <nav className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="w-6 h-6 text-cyan-500" />
            <span className="text-xl font-bold text-white tracking-tight">RJ MedGraph AI</span>
          </div>
          <button 
            onClick={onLaunch}
            className="bg-cyan-600 hover:bg-cyan-500 text-white px-5 py-2 rounded-full text-sm font-medium transition-all shadow-lg hover:shadow-cyan-500/20 flex items-center gap-2"
          >
            Launch Studio <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="relative pt-20 pb-32 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-cyan-500/10 rounded-full blur-3xl -z-10 pointer-events-none"></div>
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800/50 border border-slate-700 text-cyan-400 text-xs font-medium mb-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <Zap className="w-3 h-3" /> Now with Live Anatomy Video (Veo)
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight tracking-tight animate-in fade-in slide-in-from-bottom-6 duration-700 delay-100">
            Medical Illustration, <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600">Reimagined.</span>
          </h1>
          <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto leading-relaxed animate-in fade-in slide-in-from-bottom-6 duration-700 delay-200">
            Generate anatomical accuracy without prompt engineering. Designed for medical professionals, educators, and researchers.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-in fade-in slide-in-from-bottom-6 duration-700 delay-300">
            <button 
              onClick={onLaunch}
              className="px-8 py-4 bg-white text-slate-950 rounded-lg font-bold text-lg hover:bg-cyan-50 transition-colors shadow-xl w-full sm:w-auto"
            >
              Start Creating
            </button>
            <a href="#features" className="px-8 py-4 bg-slate-800 text-white rounded-lg font-medium hover:bg-slate-700 transition-colors border border-slate-700 w-full sm:w-auto text-center">
              Explore Features
            </a>
          </div>
        </div>
      </header>

      {/* Features Grid */}
      <section id="features" className="py-24 bg-slate-900/50 border-y border-slate-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Precision Tools for Medicine</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">Stop struggling with generic AI. RJ MedGraph uses structured prompting and fine-tuned logic to deliver clinical-grade results.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <FeatureCard 
              icon={<Brain className="w-8 h-8 text-indigo-400" />}
              title="Structured Taxonomy"
              description="Navigate via medical systems (Skeletal, Nervous, Cardio) rather than guessing words. Built-in anatomical logic."
            />
            <FeatureCard 
              icon={<Video className="w-8 h-8 text-rose-400" />}
              title="Live Video Generation"
              description="Powered by Google Veo. Generate cinematic animations of functional anatomy, from beating hearts to neural pathways."
            />
            <FeatureCard 
              icon={<FileText className="w-8 h-8 text-orange-400" />}
              title="Publication Ready"
              description="Export directly to PowerPoint with editable labels, or download High-Res TIFFs (300 DPI) for journals."
            />
            <FeatureCard 
              icon={<Layers className="w-8 h-8 text-cyan-400" />}
              title="Series Consistency"
              description="Generate a 'Series' to get 3 matching views (Master, Cross-section, Micro) of the same subject instantly."
            />
             <FeatureCard 
              icon={<MonitorPlay className="w-8 h-8 text-emerald-400" />}
              title="Iterative In-Painting"
              description="Fix specific regions without regenerating the whole image. Highlight a kidney and type 'remove cyst'."
            />
             <FeatureCard 
              icon={<Shield className="w-8 h-8 text-slate-400" />}
              title="HIPAA & Privacy"
              description="Built-in anonymization tools automatically detect and blur faces in uploaded reference images."
            />
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
           <div className="flex flex-col md:flex-row items-center gap-12">
              <div className="flex-1 space-y-8">
                <h2 className="text-3xl font-bold text-white">From Concept to Slide Deck in Seconds</h2>
                
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-900/50 text-cyan-400 flex items-center justify-center font-bold border border-cyan-800">1</div>
                  <div>
                    <h3 className="font-bold text-white text-lg">Select Anatomy</h3>
                    <p className="text-slate-400">Choose System, Region, and Pathology from structured dropdowns.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-900/50 text-cyan-400 flex items-center justify-center font-bold border border-cyan-800">2</div>
                  <div>
                    <h3 className="font-bold text-white text-lg">Define Style</h3>
                    <p className="text-slate-400">Switch between Netter-style illustration, MRI, or Hyper-realistic 3D.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-900/50 text-cyan-400 flex items-center justify-center font-bold border border-cyan-800">3</div>
                  <div>
                    <h3 className="font-bold text-white text-lg">Label & Export</h3>
                    <p className="text-slate-400">Drag & drop labels, then export directly to PPTX for your lecture.</p>
                  </div>
                </div>
              </div>
              
              <div className="flex-1 relative">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl blur-2xl opacity-20"></div>
                <div className="relative bg-slate-900 border border-slate-700 rounded-2xl p-2 shadow-2xl rotate-2 hover:rotate-0 transition-transform duration-500">
                   <div className="aspect-video bg-slate-950 rounded overflow-hidden relative">
                      {/* UI Mockup */}
                      <div className="absolute top-0 left-0 w-1/3 h-full border-r border-slate-800 bg-slate-900 p-4 space-y-4">
                        <div className="h-4 w-3/4 bg-slate-800 rounded animate-pulse"></div>
                        <div className="h-8 w-full bg-slate-800 rounded"></div>
                        <div className="h-8 w-full bg-slate-800 rounded"></div>
                        <div className="h-20 w-full bg-slate-800 rounded mt-8"></div>
                      </div>
                      <div className="absolute top-4 right-4 bg-cyan-500/20 text-cyan-400 px-3 py-1 rounded-full text-xs border border-cyan-500/50">
                        Generating...
                      </div>
                      <div className="absolute top-1/2 left-2/3 -translate-x-1/2 -translate-y-1/2">
                         <Activity className="w-16 h-16 text-slate-800" />
                      </div>
                   </div>
                </div>
              </div>
           </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 border-t border-slate-900 py-12 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
           <div className="text-slate-500 text-sm flex flex-col md:items-start gap-1">
             <div>© {new Date().getFullYear()} RJ MedGraph AI. All rights reserved.</div>
             <div className="text-slate-600 text-xs">Copyrights Flugelsoft Labs.</div>
           </div>
           <div className="text-slate-600 text-xs max-w-md text-center md:text-right">
             DISCLAIMER: AI Generated images are for educational and illustrative purposes only and should not be used for primary diagnosis or surgical planning without verification.
           </div>
        </div>
      </footer>
    </div>
  );
};

const FeatureCard: React.FC<{ icon: React.ReactNode, title: string, description: string }> = ({ icon, title, description }) => (
  <div className="p-6 rounded-xl bg-slate-800/50 border border-slate-700/50 hover:bg-slate-800 hover:border-cyan-500/30 transition-all group">
    <div className="mb-4 p-3 bg-slate-900 rounded-lg w-fit group-hover:scale-110 transition-transform">{icon}</div>
    <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
    <p className="text-slate-400 text-sm leading-relaxed">{description}</p>
  </div>
);

export default LandingPage;